import TableCell from '@material-ui/core/TableCell'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  PolicyRule_VehicleTypesEnum,
  DomainPermission_VerbEnum,
  DomainPermission_DomainEnum,
  PolicyRule_ProrationKindEnum,
} from 'src/api/gen/api'
import { IPolicyRuleVehicleDeployment } from 'src/api/types/policy'
import { Can } from 'src/commons/permissions'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { DeleteRuleButton } from 'src/components/Button'
import { ProrationField } from 'src/components/fields/Proration/ProrationField'
import NumericInput from 'src/components/Input/NumericInput'
import { SimpleTable } from 'src/components/Table'
import { FieldTitle } from 'src/pages/Policies/commons'

import { GeographiesField, RuleNameField, VehicleTypesField } from '../fields'

type Row = [
  TRulesValidation,
  string, // ruleId
  string, // name
  PolicyRule_VehicleTypesEnum[], // vehicleTypes
  string[], // geographies
  number, // rateAmount
  PolicyRule_ProrationKindEnum | undefined, // proration_kind
  Record<string, number> | undefined, // proration_areas_mapping
  string | undefined // proration_time_spent_report_area
]

export const useVehicleDeploymentRule = (
  rules: IPolicyRuleVehicleDeployment[],
  handleChange: (rules: IPolicyRuleVehicleDeployment[]) => void,
  validation: TRulesValidation[]
) => {
  const { t } = useTranslation()

  const headers = [
    '',
    t('headers.name'),
    t('headers.vehicle_types'),
    t('headers.geographies'),
    t('headers.feePerVehicle'),
    t('headers.proration'),
  ]

  const handleChangeRule =
    (ruleId: string, key?: keyof IPolicyRuleVehicleDeployment) =>
    (
      val:
        | string
        | PolicyRule_VehicleTypesEnum[]
        | number
        | string[]
        | number
        | {
            proration_areas_mapping: Record<string, number> | undefined
            proration_kind: PolicyRule_ProrationKindEnum | undefined
            proration_time_spent_report_area: string | undefined
          } // Needed because multiple setState called in the same function would run asynchronously,
      // hence reverting each other
    ): void => {
      const newRules = rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }

        if (typeof val === 'object' && 'proration_kind' in val) {
          return { ...rule, ...val }
        }

        if (key === undefined) {
          throw new TypeError('key cannot be undefined if val is not an object')
        }

        return { ...rule, [key]: val }
      })

      handleChange(newRules)
    }

  const items = rules.map(
    (rule: IPolicyRuleVehicleDeployment, index): Row => [
      validation[index],
      rule.rule_id,
      rule.name,
      rule.vehicle_types,
      rule.geographies,
      rule.rate_amount,
      rule.proration_kind,
      rule.proration_areas_mapping,
      rule.proration_time_spent_report_area,
    ]
  )

  return { headers, items, handleChangeRule }
}

const VehicleDeploymentRule = (props: IBaseTableProps<IPolicyRuleVehicleDeployment>) => {
  const { t } = useTranslation()
  const { headers, items, handleChangeRule } = useVehicleDeploymentRule(
    props.rules,
    props.handleChange,
    props.validation
  )

  return (
    <SimpleTable
      headerRow={headers.map((title: string) => (
        <TableCell key={`key-${title}`}>
          <FieldTitle title={title} />
        </TableCell>
      ))}
      items={items}
      rowRenderer={([
        ruleValidator,
        ruleId,
        name,
        vehicleTypes,
        geographies,
        rate_amount,
        prorationKind,
        prorationAreasMapping,
        prorationTimeSpentReportArea,
      ]: Row) => (
        <>
          <TableCell style={{ verticalAlign: 'middle', paddingTop: '2%' }} align="left" padding={'none'}>
            <Can I={DomainPermission_VerbEnum.write} a={DomainPermission_DomainEnum.fee_rates}>
              {props.handleDelete && (
                <DeleteRuleButton
                  label={t('buttons.delete.rule')}
                  disabled={props.disabled}
                  onClick={props.handleDelete(ruleId)}
                />
              )}
            </Can>
          </TableCell>

          <TableCell id="fee_rule_field_name" style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
            <RuleNameField
              value={name}
              handleChange={handleChangeRule(ruleId, 'name')}
              disabled={props.disabled}
              error={!ruleValidator.isFieldValid('name')}
              helperText={ruleValidator.getFieldErrorMsg('name')}
            />
          </TableCell>

          <TableCell
            id="fee_rule_field_vehicle_type"
            style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '280px' }}
            align="justify"
          >
            <VehicleTypesField
              value={vehicleTypes}
              handleChange={handleChangeRule(ruleId, 'vehicle_types')}
              disabled={props.disabled}
              error={!ruleValidator.isFieldValid('vehicle_types')}
              helperText={ruleValidator.getFieldErrorMsg('vehicle_types')}
            />
          </TableCell>

          <TableCell
            id="fee_rule_field_geography"
            style={{ verticalAlign: 'bottom', height: '100px', maxWidth: '280px' }}
            align="justify"
          >
            <GeographiesField
              value={geographies}
              allGeographies={props.allGeographies}
              handleChange={handleChangeRule(ruleId, 'geographies')}
              disabled={props.disabled}
              error={!ruleValidator.isFieldValid('geographies')}
              helperText={ruleValidator.getFieldErrorMsg('geographies')}
            />
          </TableCell>

          <TableCell
            id="fee_rule_field_fees"
            style={{ verticalAlign: 'bottom', height: '100px', maxWidth: '200px' }}
            align="right"
          >
            <NumericInput
              value={rate_amount}
              onChange={handleChangeRule(ruleId, 'rate_amount')}
              disabled={props.disabled}
              currency={props.currencySymbol}
            />
          </TableCell>

          <TableCell id="fee_rule_field_proration" style={{ height: '100px', maxWidth: '200px' }} align="right">
            <ProrationField
              disabled={props.disabled}
              prorationKind={prorationKind}
              prorationAreasMapping={prorationAreasMapping}
              areas={props.allGeographies.filter((areaLabelResponse) => geographies.includes(areaLabelResponse.id))}
              prorationTimeSpentReportArea={prorationTimeSpentReportArea}
              handleProrationChange={handleChangeRule(ruleId)}
            />
          </TableCell>
        </>
      )}
    />
  )
}

export default VehicleDeploymentRule
